import { API_TOKEN_RESOURCES } from '@/api'

/**
 * #622: what the signed-in person may reach, as the portal reads it.
 *
 * The vocabulary is the one the whole feature already speaks: `<resource>:<level>` over the base resources
 * (`packages/db/src/api-token.ts`), which is what `SessionGuard` checks every request against (#617), what
 * `GET /auth/me` now reports for the signed-in person, and what the People screen shows an owner as scope
 * chips (#618). Nothing new is invented here; this module only decides which *screen* belongs to which
 * resource, so the nav and the router can ask one question in one place.
 *
 * This is not the boundary. The server refuses a request the grant does not cover whatever the portal drew,
 * which is a separate item; hiding a destination nobody may use is about the product not reading as broken.
 */

export type PortalResource = (typeof API_TOKEN_RESOURCES)[number]

/**
 * The signed-in person's grant, in the same shape `GET /auth/me` carries. `platformAdmin` is not part of any
 * enterprise's grant: it is the flag on the person that opens the Staff area, and nothing an owner holds implies it.
 */
export type NavGrant = { isOwner?: boolean; scopes?: string[] | null; platformAdmin?: boolean }

/**
 * The Staff area: what is the same for every enterprise and no enterprise owns. Its screens answer to the
 * platform-admin flag alone, so unlike every other screen they are closed to an owner and closed when the API
 * reported no grant at all: an offer here is never made by default.
 */
export const STAFF_SCREEN = '/staff'

/** Whether `path` is one of the Staff area's screens. */
export function isStaffScreen(path: string): boolean {
    const target = path.split('?')[0] ?? ''
    return target === STAFF_SCREEN || target.startsWith(`${STAFF_SCREEN}/`)
}

/**
 * Which resource a portal screen's data comes from, longest-prefix first.
 *
 * A screen is listed only where a single base resource answers for it, because that is the only case the
 * portal can decide: `/chat` reads `/v1/chats/…`, `/agents` reads `/v1/llm/connections`, `/api-tokens`
 * reads `/v1/enterprise/api-tokens`. A path spelled differently from its resource is exactly why this is a
 * table rather than the first path segment.
 *
 * Deliberately absent, and each for a reason rather than by omission:
 * - `/` and `/enterprise`: the one destination guaranteed to every signed-in person, so a narrow grant still
 *   lands somewhere (the CTO's answer to "where does an unauthorized deep link go?" — here).
 * - `/enterprises`, `/consent`: about the account and about granting access, not about the enterprise's work.
 * - `/account`: what the person themselves holds and who invited them (#630). It explains a narrow grant, so
 *   it has to open for the narrowest one — a person holding nothing included — and it reads only `/auth/me`.
 * - `/decide/:token`: a Needs you email's one-time link (#1778). It needs no sign-in at all: the token in the
 *   address is the credential, and the API answers it for the one gate and the one owner it was minted for.
 * - `/inbox`: the person's own mailbox. It reads `/v1/mail`, which a signed-in person reaches whatever their grant
 *   (`isOwnMailRoute`), so it opens for the narrowest grant too; the nav offers it only once they have an address.
 *   `/inbox/:emailId` is one mail of it on its own page, read from the same route.
 */
const SCREEN_RESOURCES: ReadonlyArray<readonly [string, PortalResource]> = [
    // The team Activity report (#2123) is read from runs, and `/v1/activity` is served under `runs`, as Waiting is.
    ['/activity', 'runs'],
    ['/agents', 'llm'],
    ['/api-tokens', 'enterprise'],
    // The Billing page reads and writes `/v1/billing/…`: the plan, usage, storage, run slots, payment methods and
    // invoices (os#1615, #1621, #1622). The `billing` grant covers it, so a finance person needs no owner's account.
    ['/billing', 'billing'],
    ['/blockers', 'blockers'],
    ['/catalog', 'catalog'],
    ['/chat', 'chats'],
    // The other credential that can reach this enterprise (#333), served by `/v1/enterprise/connections`
    // exactly as API tokens are — so the same `enterprise` scope opens both screens.
    ['/connected-apps', 'enterprise'],
    // Attached domains (#422), served by `/v1/enterprise/domains`. Only an owner reads or changes them; the API
    // says so to anybody else who holds `enterprise`.
    ['/domains', 'enterprise'],
    ['/executions', 'executions'],
    ['/gates', 'gates'],
    ['/members', 'members'],
    // The Memory screen reads `/v1/memory/…` (the knowledge graph, #690), so it belongs to `memory` like
    // every other read of the store. Team labels on a node organise knowledge, they never fence it off.
    ['/memory', 'memory'],
    // Onboarding's AI account step (#1716) reads and writes `/v1/llm/ai-account`, so it opens for whoever holds `llm`.
    ['/onboarding/ai-account', 'llm'],
    // The guided setup screens (#1742) read and write `/v1/enterprise/onboarding`, served under `enterprise`.
    ['/onboarding/guided', 'enterprise'],
    // Onboarding's team setup step (#1739) reads and writes `/v1/enterprise/onboarding`, served under `enterprise`.
    ['/onboarding/team-setup', 'enterprise'],
    // The optional tools step (#1942) reads and writes `/v1/enterprise/onboarding` too, under `enterprise`.
    ['/onboarding/more-tools', 'enterprise'],
    ['/plans', 'plans'],
    ['/roles', 'roles'],
    ['/runs', 'runs'],
    ['/self-improvement', 'recommendations'],
    ['/spend', 'spend'],
    ['/tasks', 'tasks'],
    ['/teams', 'teams'],
    ['/tools', 'tools'],
    // Who can reach the enterprise (#619). Only an owner reads the lists on it, but `users:write` is what lets
    // anybody else invite somebody from it, so the screen is offered to whoever holds `users` at all.
    ['/users', 'users'],
    // The Waiting list (#1457) is open runs nothing is working, and `/v1/waiting` is served under `runs` (#1678), so
    // the screen is offered to whoever may read runs: exactly who the API serves it to.
    ['/waiting', 'runs'],
    // A work item's page (#1830) reads `/v1/work-items/:id` and its history, which ride the team grant (#1827).
    ['/work-items', 'teams'],
    // Webhook history and one receipt's permanent link are the same resource under two spellings.
    ['/webhook', 'webhooks'],
    ['/webhooks', 'webhooks'],
    // Work's merged view (#1747) reads the execution board and one execution's detail, both served under
    // `executions`, so it opens for exactly the grants the Executions page opens for.
    ['/work', 'executions'],
]

/**
 * The screens above, named as a list rather than only as a gap in the table.
 *
 * `resourceForScreen` fails open by design (a path it does not know is not restricted), which is right for
 * these and wrong for a protected screen somebody adds later without a row in `SCREEN_RESOURCES` —
 * that screen would be reachable by the narrowest grant, silently. Naming the unscoped ones here lets
 * `navGrant.spec.ts` walk the router's real routes and fail CI on any screen that is neither mapped nor
 * deliberately open, so the omission is caught before it ships rather than after.
 */
export const UNSCOPED_SCREENS: readonly string[] = [
    '/',
    '/enterprise',
    '/enterprises',
    '/consent',
    '/account',
    '/decide/:token',
    '/inbox',
    '/inbox/:emailId',
]

/** The resource a screen belongs to, or null where the portal has nothing to check it against. */
export function resourceForScreen(path: string): PortalResource | null {
    const target = (path.split(/[?#]/)[0] ?? '').replace(/\/+$/, '') || '/'
    let found: PortalResource | null = null
    let matched = ''
    for (const [screen, resource] of SCREEN_RESOURCES) {
        if ((target === screen || target.startsWith(`${screen}/`)) && screen.length > matched.length) {
            found = resource
            matched = screen
        }
    }
    return found
}

/**
 * Whether a grant says nothing at all, in which case the portal restricts nothing.
 *
 * Unlike the server, silence here is not a refusal: an API older than #622 reports neither field, and the
 * portal blanking its own navigation on a missing field would be a worse failure than showing a destination
 * whose refusal the server explains. The enforcement point that must fail closed is `SessionGuard`, and it
 * does.
 */
function grantUnknown(grant: NavGrant | null | undefined): boolean {
    return !grant || (grant.isOwner === undefined && grant.scopes === undefined)
}

/** Whether `grant` covers `resource` at any level. An owner holds everything, with nothing enumerated. */
export function grantCovers(grant: NavGrant | null | undefined, resource: PortalResource | null): boolean {
    if (resource === null || grantUnknown(grant)) {
        return true
    }
    if (grant?.isOwner === true) {
        return true
    }
    const scopes = grant?.scopes ?? []
    // `write` implies `read` on the same resource, as it does server-side, so either opens the screen.
    return scopes.includes(`${resource}:read`) || scopes.includes(`${resource}:write`)
}

/**
 * Whether `grant` may change `resource`: `<resource>:write`, which `read` alone does not give. `grantCovers` answers
 * whether a screen opens at all; this answers whether a control on it may be operated (#1316). An owner holds it, and
 * a grant the API did not report restricts nothing here either, for the reason `grantUnknown` gives: the server
 * refuses a write the grant does not cover, and the screen shows that refusal.
 */
export function grantWrites(grant: NavGrant | null | undefined, resource: PortalResource): boolean {
    if (grantUnknown(grant) || grant?.isOwner === true) {
        return true
    }
    return (grant?.scopes ?? []).includes(`${resource}:write`)
}

/** Whether `grant` may open the screen at `path`. */
export function grantAllowsScreen(grant: NavGrant | null | undefined, path: string): boolean {
    if (isStaffScreen(path)) {
        return grant?.platformAdmin === true
    }
    return grantCovers(grant, resourceForScreen(path))
}
